import { Component, Inject, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonService } from '../../services/common.service';
@Component({
  selector: 'app-add-attachment',
  templateUrl: './add-attachment.component.html',
  styleUrl: './add-attachment.component.scss'
})
export class AddAttachmentComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly dialogRef: MatDialogRef<AddAttachmentComponent>,
    private readonly commonService: CommonService
  ) { }
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement> | undefined;
  files: File[] = [];
  selectedFiles: File[] = [];
  isFileCountExceeded: boolean = false;
  isUploading: boolean = false;
  uploadSuccess: boolean = false;
  uploadFailure: boolean = false;
  uploadLimitFailure: boolean = false;
  allowedFileTypes: string[] = [];
  maxFileSizeMB = 0;
  maxFilesLimit = 0;
  invalidFileTypes: string[] = [];
  oversizedFiles: string[] = [];


  ngOnInit() {
    this.getUploadFileOptions();
  }
  onCancelClick(): void {
    this.dialogRef.close(this.uploadSuccess);
  }

  getReadableFileTypes(): string {
    return this.allowedFileTypes
      .map(ext => ext.replace('.', '').toUpperCase())
      .join('/');
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'copy'; // Show as copy when dragging over
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
  }

  getUploadFileOptions(): void {
    this.commonService.getUploadFileOptions().subscribe({
      next: (response: UploadOptionsResponse) => {
        if (response) {
          const sizeMatch = response.sizeLimit.match(/(\d+)/);
          this.maxFileSizeMB = sizeMatch ? parseInt(sizeMatch[1], 10) : 100;
          this.maxFilesLimit = response.filesLimit;
          this.allowedFileTypes = response.fileTypes
            .split(',')
            .map((ext: string) => ext.trim().toUpperCase());
        }
      },
      error: () => {
        console.error('Failed to fetch upload options.');
      }
    });
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const selectedFiles = Array.from(input.files);
    this.validateAndAddFiles(selectedFiles);
    input.value = ''; // reset input
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer?.files || []);
    this.validateAndAddFiles(droppedFiles);
  }

  validateAndAddFiles(fileList: File[]): void {
    this.uploadSuccess = false;
    this.uploadFailure = false;
    this.uploadLimitFailure = false;
    this.invalidFileTypes = [];
    this.oversizedFiles = [];

    if ((this.files.length + fileList.length) > this.maxFilesLimit) {
      this.uploadLimitFailure = true;
      return;
    }

    for (const file of fileList) {
      const ext = '.' + (file.name.split('.').pop()?.toUpperCase() || '');

      if (!this.allowedFileTypes.includes(ext)) {
        this.invalidFileTypes.push(file.name);
        continue;
      }

      if (file.size > this.maxFileSizeMB * 1024 * 1024) {
        this.oversizedFiles.push(file.name);
        continue;
      }

      if (this.files.length < this.maxFilesLimit) {
        this.files.push(file);
      }
    }
  }

  removeFile(index: number, name: string): void {
    this.files.splice(index, 1);
  }

  isAllFilesValid(): boolean {
    return this.files.every(file => {
      const ext = '.' + file.name.split('.').pop()?.toUpperCase();
      return this.allowedFileTypes.includes(ext) &&
        file.size <= this.maxFileSizeMB * 1024 * 1024;
    });
  }

  uploadFiles(): void {
    if (this.isUploading) return; // prevent multiple uploads
    this.isUploading = true; 

    // Iterate through newly selected files and add to selectedFiles if not already present
    this.files.forEach((newFile) => {
      const existingFile = this.selectedFiles.find(
        (file) => file.name === newFile.name
      );
      if (!existingFile) {
        this.selectedFiles.push(newFile);
      }
    });

    if (this.selectedFiles.length > 0) {
      this.isUploading = true;

      this.commonService.uploadAttachments(
        this.selectedFiles,
        this.data.userGpid,
        this.data.systemId,
        this.data.documentId
      ).subscribe({
        next: (response) => {
          this.isUploading = false;

          if (response === "" || response === null) {
            this.uploadFailure = true;
          } else if (response == "0" || response.includes("You cannot add more than")) {
            this.uploadLimitFailure = true;
          } else {
            this.uploadSuccess = true;
            this.selectedFiles = [];
            this.files = [];
          }
        },
        error: (error) => {
          this.isUploading = false;
          this.uploadFailure = true;
          this.selectedFiles = [];
          this.files = [];
        }
      });
    } else {
      this.isUploading = false; // reset if no files
    }
  }

  resetImage(): void {
    this.files = [];
    this.selectedFiles = [];
  }
}


interface UploadOptionsResponse {
  sizeLimit: string; // example: "100 MB"
  filesLimit: number;
  fileTypes: string;
}